import { Translator } from 'Translator';
import InputNumberOfPeopleSelector from 'components/InputNumberOfPeople/InputNumberOfPeopleSelector';
import Modal from 'components/Modal';
import RoundedButton from 'components/RoundedButton';
import ChevronIcon from 'components/icons/ChevronIcon';
import useIsMobile from 'hooks/useIsMobile';
import { ExperienceSource } from 'model/IExperience';
import React from 'react';
import { Trek } from 'utils/analytics';
import { formatPrice } from 'utils/formatPrice';
import { getPricePerPerson } from 'utils/getPrettyPrice';
import { useCurrencyContext } from '~/context/CurrencyContext';

const CSS = require('./PeopleSelector.module.scss');

interface PeopleSelectorProps {
  onClose: () => void;
  onNumberOfPeopleChanged: (adults, children) => void;
  maxPeople?: number;
  adults: number;
  children: number;
  experience?: ExperienceSource;
  onSave?: () => void;
  buttonText?: string;
  customizationLink?: string;
  hideContactButton?: boolean;
  selectedDate?: string;
  selectedTime?: string;
}

function PeopleSelector({
  adults,
  customizationLink,
  children,
  maxPeople,
  onClose,
  onSave,
  onNumberOfPeopleChanged,
  experience,
  buttonText,
  hideContactButton,
  selectedDate,
  selectedTime,
}: PeopleSelectorProps) {
  const isMobile = useIsMobile();
  const { state } = useCurrencyContext();

  const getFormattedPrice = () => {
    const price = getPricePerPerson(experience, adults, children);
    return formatPrice(price, state.currency);
  };

  const peopleSelectorContent = (
    <Trek
      context={{
        nrOfAdults: adults,
        nrOfChildren: children,
        component: ['people-selector'],
      }}
    >
      <div className={CSS['PeopleSelectorOverlay-content']}>
        <div className={CSS['PeopleSelectorOverlay-topbar']}>
          <button
            className={CSS['PeopleSelectorOverlay-back']}
            onClick={onClose}
          >
            <ChevronIcon
              width={8}
              height={14}
              color="#4A4A4A"
              direction="left"
              className={CSS['PeopleSelectorOverlay-back-icon']}
            />
          </button>
          {!isMobile && (
            <span className={CSS['PeopleSelectorOverlay-modalTitle']}>
              {Translator.translate(
                'Forms.Labels.numberOfPeople',
                {},
                true,
                'Number of people',
              )}
            </span>
          )}
        </div>
        {isMobile && (
          <span className={CSS['PeopleSelectorOverlay-title']}>
            {Translator.translate(
              'Forms.Labels.numberOfPeople',
              {},
              true,
              'Number of people',
            )}
          </span>
        )}
        <div className={CSS['PeopleSelectorOverlay-selector']}>
          <InputNumberOfPeopleSelector
            customizationLink={customizationLink}
            onChange={onNumberOfPeopleChanged}
            nrOfAdultsInitialValue={adults}
            nrOfChildrenInitialValue={children}
            maxPeople={!!maxPeople ? maxPeople : 20}
            hideContactButton={hideContactButton}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
          />
          {experience && state.currency && (
            <div>
              <div className={CSS.ppPricing}>
                <span>
                  {Translator.translate(
                    'CheckoutPage.BookingDetails.pricePerPerson',
                    {},
                    true,
                    'Price per person',
                  )}
                  <span className={CSS.pricing}> {getFormattedPrice()}</span>
                </span>
              </div>
            </div>
          )}
        </div>
        <div className={CSS['PeopleSelectorOverlay-SaveHolder']}>
          <RoundedButton
            fullWidth
            edgeRounding="medium"
            onClick={onSave || onClose}
            pink
          >
            {buttonText ||
              Translator.translate('Forms.Buttons.save', {}, true, 'Save')}
          </RoundedButton>
        </div>
      </div>
    </Trek>
  );

  return (
    <Modal
      className={CSS['PeopleSelectorModal']}
      show
      onClose={onClose}
      fullscreen={isMobile}
      hideClose
    >
      {peopleSelectorContent}
    </Modal>
  );
}

export default PeopleSelector;
